import { register } from '../../../../Javascript/Sysmex/Utils'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
// import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper.js'
// import * as dat from 'lil-gui'

const Graphic3D = el => {
  const filePath = el.dataset.filePath
  const prefix = el.dataset.prefix
  const isSquare = el.dataset.isSquare || false

  const sceneSizes = {
    width: 0,
    height: 0
  }

  // Canvas
  const canvas = el.querySelector(`.${prefix}container`)
  const loadingIndicator = el.querySelector(`.${prefix}loader`)

  // Scene
  const scene = new THREE.Scene()

  /**
  * Model
  */
  const dracoLoader = new DRACOLoader()
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')

  const gltfLoader = new GLTFLoader()
  gltfLoader.setDRACOLoader(dracoLoader)

  gltfLoader.load(filePath, gltf => {
    const sceneBox = new THREE.Box3().setFromObject(gltf.scene)

    const sceneCenter = sceneBox.getCenter(new THREE.Vector3())
    const sceneSize = sceneBox.getSize(new THREE.Vector3())

    // el.style.aspectRatio = `${sceneSize.x.toFixed(2)} / ${sceneSize.y.toFixed(2)}`
    // const aspectRatio = `${sceneSize.x.toFixed(2)} / ${sceneSize.y.toFixed(2)}`
    sceneSizes.width = sceneSize.x
    sceneSizes.height = sceneSize.y

    // const sceneSizeArray = Object.values(sceneSize)
    // const sceneMaxSize = Math.max(...sceneSizeArray)
    let sceneMaxSize

    if (sceneSize.x > sceneSize.y) {
      sceneMaxSize = Math.min(sceneSize.x, sceneSize.y)
    } else {
      sceneMaxSize = Math.max(sceneSize.x, sceneSize.y)
    }

    gltf.scene.position.x += (gltf.scene.position.x - sceneCenter.x)
    gltf.scene.position.y += (gltf.scene.position.y - sceneCenter.y)
    gltf.scene.position.z += (gltf.scene.position.z - sceneCenter.z)

    scene.add(gltf.scene)

    camera.position.set(0, 0, 2.8 * sceneMaxSize)

    resizeCanvas()
    loadingIndicator.classList.remove(`${prefix}is--active`)
  })

  /**
   * Lights
   */
  // Ambient light
  const ambientLight = new THREE.AmbientLight()
  ambientLight.color = new THREE.Color(0xffffff)
  ambientLight.intensity = 1
  scene.add(ambientLight)

  // Hemisphere light
  const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.7)
  hemisphereLight.position.set(0, 1.5, 0)
  scene.add(hemisphereLight)

  // Directional light
  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.85)
  directionalLight.position.set(1, 0.5, 3)
  scene.add(directionalLight)

  // Point light
  const pointLight = new THREE.PointLight(0xffffff, 0.5, 5, 2)
  // parameters: color, intensity, distance, decay
  pointLight.position.set(1, 0, 2)
  scene.add(pointLight)

  // Rect area light
  const rectAreaLight = new THREE.RectAreaLight(0xffffff, 2, 1, 1)
  rectAreaLight.position.set(-1.5, 0, 2)
  rectAreaLight.lookAt(new THREE.Vector3())
  scene.add(rectAreaLight)

  // Spot light
  const spotLight = new THREE.SpotLight(0xffffff, 0.25, 9, Math.PI * 0.1, 0.25, 5)
  // Parameters:
  // color: the color
  // intensity: the strength
  // distance: the distance at which the intensity drops to 0
  // angle: how large is the beam
  // penumbra: how diffused is the contour of the beam
  // decay: how fast the light dims
  spotLight.position.set(0, 2, 5)
  scene.add(spotLight)

  spotLight.target.position.x = -0.75
  scene.add(spotLight.target)

  // Helpers
  // const hemisphereLightHelper = new THREE.HemisphereLightHelper(hemisphereLight, 0.2)
  // scene.add(hemisphereLightHelper)

  // const directionalLightHelper = new THREE.DirectionalLightHelper(directionalLight, 0.2)
  // scene.add(directionalLightHelper)

  // const light = new THREE.PointLight( 0xff0000, 1, 8, 10)
  // light.position.set( 1, 1, 1)

  // const rectAreaLightHelper = new RectAreaLightHelper(rectAreaLight)
  // scene.add(rectAreaLightHelper)

  // const spotLightHelper = new THREE.SpotLightHelper(spotLight)
  // scene.add(spotLightHelper)

  /**
   * Sizes
   */
  const containerSizes = {
    width: el.offsetWidth,
    height: el.offsetHeight
  }

  const resizeCanvas = () => {
    // Update sizes
    containerSizes.width = el.offsetWidth

    // Update camera
    camera.aspect = isSquare ? containerSizes.width / containerSizes.width : sceneSizes.width / sceneSizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(containerSizes.width, isSquare ? containerSizes.width : containerSizes.width / sceneSizes.width * sceneSizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  }

  window.addEventListener('resize', resizeCanvas)

  /**
   * Camera
   */
  // Base camera
  const camera = new THREE.PerspectiveCamera(35, containerSizes.width / isSquare ? containerSizes.width : (containerSizes.width / 2), 0.1, 100)

  scene.add(camera)

  // Controls
  const controls = new OrbitControls(camera, canvas)
  controls.target.set(0, -0.05, 0)
  controls.enableDamping = true
  controls.autoRotate = true
  controls.enableZoom = true
  // controls.enabled = false

  const userAction = () => {
    controls.autoRotate = false
  }

  controls.addEventListener('start', userAction)

  /**
   * Renderer
   */
  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true
  })
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  // set loading size
  renderer.setSize(containerSizes.width, isSquare ? containerSizes.width : containerSizes.width / 2)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.physicallyCorrectLights = true
  renderer.outputEncoding = THREE.sRGBEncoding
  renderer.toneMapping = THREE.LinearToneMapping
  renderer.toneMappingExposure = 1

  /**
  * Debug
  */
  // const gui = new dat.GUI()
  // gui.add(ambientLight, 'intensity').min(0).max(1).step(0.001)
  // gui.add(pointLight.position, 'z', 0, 10)

  /**
   * Animate
   */
  const tick = () => {
    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
  }

  tick()
}

register('Graphic3D', Graphic3D)
